/**
 * PlatformHotelDetail — Super Admin view of one hotel.
 * Actions: Switch into Hotel · Edit hotel · Create Administrator · Soft-delete
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, AlertTriangle, CheckCircle2, Building2,
  UserPlus, LogIn, Copy, Pencil, Trash2, Loader2, RotateCcw, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  draft:        { label: "Draft",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
  trial:        { label: "Trial",       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  active:       { label: "Active",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  suspended:    { label: "Suspended",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
  archived:     { label: "Archived",    cls: "bg-slate-100 text-slate-500 border-slate-300" },
  soft_deleted: { label: "Deleted",     cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

const STATUS_OPTIONS = ["draft", "trial", "active", "suspended", "archived", "soft_deleted"];

const COMMON_TIMEZONES = [
  "UTC", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York",
  "America/Los_Angeles", "America/Chicago", "Australia/Sydney",
];

const COMMON_CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "JPY", "AUD", "CAD"];

const COMMON_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese" },
  { code: "ar", label: "Arabic" },
];

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join(", ");
  return e?.message || "Something went wrong";
}

function Row({ label, value, mono = false }) {
  return (
    <div className="grid grid-cols-[160px_1fr] items-start gap-4 py-3 border-b border-slate-200 last:border-b-0">
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold pt-0.5">
        {label}
      </div>
      <div
        className={`text-[15px] leading-relaxed text-slate-900 font-medium ${mono ? "font-mono text-[13px]" : ""}`}
      >
        {value || <span className="text-slate-400 italic font-normal">Not set</span>}
      </div>
    </div>
  );
}

// ─── Edit Dialog ─────────────────────────────────────────────────────

function EditHotelDialog({ open, hotel, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hotel) {
      setForm({
        code: hotel.code || "",
        name: hotel.name || "",
        brand_name: hotel.brand_name || "",
        country: hotel.country || "",
        timezone: hotel.timezone || "UTC",
        currency: hotel.currency || "",
        default_language: hotel.default_language || "en",
        status: hotel.status || "draft",
      });
    }
  }, [hotel]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    if (form.currency && form.currency.length !== 3) return toast.error("Currency must be a 3-letter ISO code");
    // Validate code shape before hitting the API — same rule as the backend model.
    const rawCode = (form.code || "").trim();
    if (!rawCode) return toast.error("Hotel code is required");
    if (!/^[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9]$/.test(rawCode)) {
      return toast.error("Hotel code must be slug-style: letters, digits, dashes only");
    }
    setSaving(true);
    try {
      // Only send `code` when it actually changed — the backend enforces
      // global uniqueness and we don't want harmless no-op saves to error.
      const codeChanged = rawCode !== (hotel?.code || "").trim();
      const { data } = await api.patch(`/platform/v1/hotels/${hotel.id}`, {
        ...(codeChanged ? { code: rawCode } : {}),
        name: form.name.trim(),
        brand_name: form.brand_name?.trim() || null,
        country: form.country?.trim() || null,
        timezone: form.timezone || null,
        currency: (form.currency || "").toUpperCase() || null,
        default_language: form.default_language || null,
        status: form.status || null,
      });
      toast.success(codeChanged ? "Hotel updated · code renamed" : "Hotel updated");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Edit hotel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Hotel code *</Label>
            <Input
              value={form.code || ""}
              onChange={(e) => set("code", e.target.value)}
              placeholder="hotel-slug"
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="edit-hotel-code"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Slug-style: letters, digits and dashes only. Must be globally unique.
              Changing this renames the hotel&apos;s identifier across the platform.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
            <Input
              value={form.name || ""}
              onChange={(e) => set("name", e.target.value)}
              className="bg-white border-slate-300 mt-1.5"
              data-testid="edit-hotel-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Brand</Label>
            <Input
              value={form.brand_name || ""}
              onChange={(e) => set("brand_name", e.target.value)}
              className="bg-white border-slate-300 mt-1.5"
              data-testid="edit-hotel-brand"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Country</Label>
              <Input
                value={form.country || ""}
                onChange={(e) => set("country", e.target.value)}
                className="bg-white border-slate-300 mt-1.5"
                placeholder="e.g. India"
                data-testid="edit-hotel-country"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Currency</Label>
              <Select value={form.currency || ""} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger className="bg-white border-slate-300 mt-1.5" data-testid="edit-hotel-currency">
                  <SelectValue placeholder="Select currency"/>
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Timezone</Label>
              <Select value={form.timezone || "UTC"} onValueChange={(v) => set("timezone", v)}>
                <SelectTrigger className="bg-white border-slate-300 mt-1.5" data-testid="edit-hotel-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Language</Label>
              <Select value={form.default_language || "en"} onValueChange={(v) => set("default_language", v)}>
                <SelectTrigger className="bg-white border-slate-300 mt-1.5" data-testid="edit-hotel-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label} ({l.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={form.status || "draft"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-white border-slate-300 mt-1.5" data-testid="edit-hotel-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_STYLE[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-hotel-submit"
            className="bg-amber-500 text-white hover:bg-amber-600 font-semibold">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Dialog (type-code confirmation) ──────────────────────────

function DeleteHotelDialog({ open, hotel, onClose, onDeleted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setText(""); }, [open]);

  const matches = text.trim() === (hotel?.code || "");

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      await api.post(`/platform/v1/hotels/${hotel.id}/delete`, { confirm_code: text.trim() });
      toast.success(`${hotel.name} has been deleted`);
      onDeleted();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500"/>
            Delete this hotel?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">
            <div className="font-semibold mb-1">This is a soft-delete.</div>
            <ul className="list-disc list-inside space-y-1 text-rose-700">
              <li>The hotel is hidden from the platform list.</li>
              <li>All hotel administrators are deactivated and cannot log in.</li>
              <li>All underlying data is preserved and recoverable.</li>
            </ul>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Type <span className="font-mono text-slate-900">{hotel?.code}</span> to confirm
            </Label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              placeholder={hotel?.code}
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="delete-hotel-confirm-input"
            />
            {text && !matches && (
              <p className="text-[12px] text-rose-600 mt-1">Code doesn&apos;t match yet.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!matches || busy}
            data-testid="delete-hotel-submit"
            className="bg-rose-500 hover:bg-rose-600 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            Delete hotel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Permanent Purge Dialog (type-code confirmation) ─────────────────

function PurgeHotelDialog({ open, hotel, onClose, onPurged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setText(""); }, [open]);

  const matches = text.trim() === (hotel?.code || "");

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/v1/hotels/${hotel.id}/purge`, { confirm_code: text.trim() });
      toast.success(`${hotel.name} permanently deleted — ${data.documents_deleted} records erased`);
      onPurged();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600"/>
            Permanently delete this hotel?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-rose-50 border border-rose-300 p-4 text-sm text-rose-800">
            <div className="font-semibold mb-1">This cannot be undone.</div>
            <ul className="list-disc list-inside space-y-1 text-rose-700">
              <li>Erases ALL data for this hotel across every module.</li>
              <li>Removes staff accounts that belong only to this hotel.</li>
              <li>There is no restore after this.</li>
            </ul>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Type <span className="font-mono text-slate-900">{hotel?.code}</span> to confirm
            </Label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              placeholder={hotel?.code}
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="purge-hotel-confirm-input"
            />
            {text && !matches && (
              <p className="text-[12px] text-rose-600 mt-1">Code doesn&apos;t match yet.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!matches || busy}
            data-testid="purge-hotel-submit"
            className="bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            Permanently delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

export default function PlatformHotelDetail() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const restoreHotel = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post(`/platform/v1/hotels/${hotelId}/restore`);
      toast.success("Hotel restored");
      setHotel(h => ({ ...h, status: data.restored_status || "active" }));
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRestoring(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/platform/v1/hotels/${hotelId}`);
      setHotel(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load hotel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [hotelId]);

  const enterHotel = async () => {
    setSwitching(true);
    try {
      await api.post("/wf/v1/switch", { hotel_id: hotelId });
      window.location.href = "/dashboard";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to switch context");
      setSwitching(false);
    }
  };

  const copyId = () => {
    if (hotel?.id) navigator.clipboard.writeText(hotel.id);
  };

  if (loading) {
    return <div className="text-slate-500" data-testid="platform-hotel-detail-loading">Loading…</div>;
  }
  if (err && !hotel) {
    return (
      <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{err}</span>
      </div>
    );
  }

  const status = STATUS_STYLE[hotel.status] || STATUS_STYLE.draft;

  return (
    <div className="space-y-8" data-testid="platform-hotel-detail">
      {/* Header */}
      <div>
        <Link
          to="/platform/hotels"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hotels
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl font-semibold text-slate-900 truncate">
                  {hotel.name}
                </h1>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider ${status.cls}`}
                    data-testid="hotel-detail-status"
                  >
                    {status.label}
                  </span>
                  <span className="text-[13px] text-slate-600 font-mono truncate">{hotel.code}</span>
                  {hotel.brand_name && (
                    <span className="text-[13px] text-slate-600">· {hotel.brand_name}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button
              data-testid="hotel-detail-edit"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-800 font-semibold text-sm hover:bg-slate-50 transition-colors shadow-sm"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
            {!hotel.has_administrator && (
              <button
                data-testid="hotel-detail-create-admin"
                onClick={() => navigate(`/platform/hotels/${hotelId}/administrator`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 shadow-sm"
              >
                <UserPlus className="w-4 h-4" />
                Create Administrator
              </button>
            )}
            <button
              data-testid="hotel-detail-switch-into"
              onClick={enterHotel}
              disabled={switching || hotel.status === "soft_deleted"}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-hocp-mint text-hocp-deepTeal font-semibold text-sm hover:bg-hocp-mintStrong disabled:opacity-50 shadow-sm"
            >
              <LogIn className="w-4 h-4" />
              {switching ? "Switching…" : "Switch into Hotel"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
          <h2 className="text-[12px] uppercase tracking-[0.16em] text-amber-600 font-bold mb-5">
            Identity
          </h2>
          <div>
            <Row label="Hotel Name" value={hotel.name} />
            <Row label="Hotel Code" value={hotel.code} mono />
            <Row label="Brand" value={hotel.brand_name} />
            <Row label="Organization" value={hotel.organization_name} />
            <Row label="Country" value={hotel.country} />
            <Row label="Timezone" value={hotel.timezone} mono />
            <Row label="Currency" value={hotel.currency} mono />
            <Row label="Default Language" value={hotel.default_language} mono />
            <Row label="Created" value={hotel.created_at} mono />
            <Row
              label="Hotel ID"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-[13px] text-slate-800">{hotel.id}</span>
                  <button
                    onClick={copyId}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                    title="Copy Hotel ID"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </span>
              }
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
            <h2 className="text-[12px] uppercase tracking-[0.16em] text-amber-600 font-bold mb-5">
              Onboarding
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {hotel.has_administrator ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-slate-300 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] text-slate-900 font-semibold">Administrator</div>
                  <div className="text-[13px] text-slate-600">
                    {hotel.has_administrator ? "Assigned" : "Not yet assigned"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
            <h2 className="text-[12px] uppercase tracking-[0.16em] text-amber-600 font-bold mb-4">
              Quick actions
            </h2>
            <div className="space-y-2">
              <button
                onClick={enterHotel}
                disabled={switching || hotel.status === "soft_deleted"}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[14px] text-slate-900 font-medium transition-colors disabled:opacity-50"
              >
                <span className="flex items-center gap-2.5">
                  <LogIn className="w-4 h-4 text-amber-600" />
                  Switch into this Hotel
                </span>
                <ArrowRight className="w-4 h-4 text-slate-500" />
              </button>
              <button
                onClick={() => setEditOpen(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[14px] text-slate-900 font-medium transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <Pencil className="w-4 h-4 text-amber-600" />
                  Edit hotel details
                </span>
                <ArrowRight className="w-4 h-4 text-slate-500" />
              </button>
              <button
                onClick={() => navigate(`/platform/hotels/${hotelId}/services`)}
                data-testid="hotel-detail-edit-services"
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[14px] text-slate-900 font-medium transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-amber-600" />
                  Edit services
                </span>
                <ArrowRight className="w-4 h-4 text-slate-500" />
              </button>
              {!hotel.has_administrator && (
                <button
                  onClick={() => navigate(`/platform/hotels/${hotelId}/administrator`)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[14px] text-slate-900 font-medium transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <UserPlus className="w-4 h-4 text-amber-600" />
                    Create Administrator
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      {hotel.status !== "soft_deleted" && (
        <div
          className="rounded-2xl bg-white border border-rose-200 p-6 shadow-sm"
          data-testid="hotel-detail-danger-zone"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-[12px] uppercase tracking-[0.16em] text-rose-600 font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4"/>
                Danger zone
              </h2>
              <p className="text-sm text-slate-700 mt-2 max-w-xl">
                Deleting a hotel hides it from the platform, deactivates all its administrators
                and prevents further logins. Data is preserved for restore.
              </p>
            </div>
            <button
              data-testid="hotel-detail-delete-btn"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-rose-300 text-rose-700 font-semibold text-sm hover:bg-rose-50 transition-colors shadow-sm"
            >
              <Trash2 className="w-4 h-4"/>
              Delete this hotel…
            </button>
          </div>
        </div>
      )}

      {hotel.status === "soft_deleted" && (
        <>
        <div
          className="rounded-2xl bg-white border border-emerald-200 p-6 shadow-sm"
          data-testid="hotel-detail-restore-zone"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-[12px] uppercase tracking-[0.16em] text-emerald-700 font-bold flex items-center gap-2">
                <RotateCcw className="w-4 h-4"/>
                Restore hotel
              </h2>
              <p className="text-sm text-slate-700 mt-2 max-w-xl">
                This hotel is soft-deleted. Restoring brings it back to its previous status,
                makes it visible on the platform again, and re-activates its administrators so
                they can log in. No data was lost.
              </p>
            </div>
            <button
              data-testid="hotel-detail-restore-btn"
              onClick={restoreHotel}
              disabled={restoring}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 border border-emerald-700 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin"/> : <RotateCcw className="w-4 h-4"/>}
              Restore hotel
            </button>
          </div>
        </div>

        <div
          className="rounded-2xl bg-white border border-rose-300 p-6 shadow-sm"
          data-testid="hotel-detail-purge-zone"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-[12px] uppercase tracking-[0.16em] text-rose-700 font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4"/>
                Permanently delete
              </h2>
              <p className="text-sm text-slate-700 mt-2 max-w-xl">
                This hotel is soft-deleted. Permanently deleting it erases <strong>all</strong> of
                its data — reservations, stays, guests, folios, payments, rooms, housekeeping,
                F&amp;B, workforce and settings — across every module. Staff accounts unique to this
                hotel are removed. <strong>This action cannot be undone.</strong>
              </p>
            </div>
            <button
              data-testid="hotel-detail-purge-btn"
              onClick={() => setPurgeOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 border border-rose-700 text-white font-semibold text-sm hover:bg-rose-700 transition-colors shadow-sm"
            >
              <Trash2 className="w-4 h-4"/>
              Permanently delete…
            </button>
          </div>
        </div>
        </>
      )}

      <EditHotelDialog
        open={editOpen}
        hotel={hotel}
        onClose={() => setEditOpen(false)}
        onSaved={(fresh) => setHotel(fresh)}
      />
      <DeleteHotelDialog
        open={deleteOpen}
        hotel={hotel}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate("/platform/hotels")}
      />
      <PurgeHotelDialog
        open={purgeOpen}
        hotel={hotel}
        onClose={() => setPurgeOpen(false)}
        onPurged={() => navigate("/platform/hotels")}
      />
    </div>
  );
}
